import React, {useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {forgotPassword, resetPassword} from '../../lib/api';
import {Card, CardContent} from '../../components/ui/card';
import {Button} from '../../components/ui/button';
import {Input} from '../../components/ui/input';
import {Label} from '../../components/ui/label';
import { ArrowLeft, Key, CircleNotch, Lock, Envelope } from '@phosphor-icons/react';
import {usePageTitle} from '@/hooks/usePageTitle';
import {getRutaWebsiteUrl} from '../../lib/constants';

type ResetMode = 'request-code' | 'reset-password';

export default function ResetPassword() {
  usePageTitle('Reset Password');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') || '';
  
  // Determine initial mode based on whether email is provided
  const [mode, setMode] = useState<ResetMode>(emailParam ? 'reset-password' : 'request-code');
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Request code state
  const [email, setEmail] = useState(emailParam);
  
  // Reset password state
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess('Password reset instructions have been sent to your email address.');
      // After successful request, switch to reset mode
      setTimeout(() => {
        switchMode('reset-password');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, confirmationCode, newPassword);
      setSuccess('Password has been reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login?email=' + encodeURIComponent(email));
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  }

  function resetState() {
    setError(null);
    setSuccess(null);
  }

  function switchMode(newMode: ResetMode) {
    setIsTransitioning(true);
    resetState();

    setTimeout(() => {
      setMode(newMode);
      setIsTransitioning(false);
    }, 150);
  }

  const getTitle = () => {
    switch (mode) {
      case 'request-code': return 'Reset Password';
      case 'reset-password': return 'Create New Password';
      default: return 'Reset Password';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'request-code': return "We'll email you reset instructions";
      case 'reset-password': return 'Enter the code from your email and create a new password';
      default: return '';
    }
  };

  const renderBackButton = () => {
    const handleBack = () => {
      if (mode === 'reset-password' && !emailParam) {
        // If we're in reset mode but didn't start with an email param, go back to request mode
        switchMode('request-code');
      } else {
        // Otherwise go back to login
        navigate('/login');
      }
    };

    return (
      <Button
        type="button"
        variant="ghost"
        onClick={handleBack}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
    );
  };

  const renderForm = () => {
    const formContent = (() => {
      switch (mode) {
        case 'request-code':
          return (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-stone-700 mb-1.5">Email Address</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading}>
                {loading ? (
                  <CircleNotch className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                {loading ? 'Sending...' : 'Send Reset Instructions'}
              </Button>
            </form>
          );

        case 'reset-password':
          return (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <Label htmlFor="resetEmail" className="text-sm font-medium text-stone-700 mb-1.5">Email Address</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="resetEmail"
                    type="email"
                    autoComplete="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required={!emailParam}
                    disabled={!!emailParam}
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirmationCode" className="text-sm font-medium text-stone-700 mb-1.5">Reset Code</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="confirmationCode"
                    type="text"
                    autoFocus
                    placeholder="Enter code from email"
                    value={confirmationCode}
                    onChange={e => setConfirmationCode(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="newPassword" className="text-sm font-medium text-stone-700 mb-1.5">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-stone-700 mb-1.5">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading}>
                {loading ? (
                  <CircleNotch className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          );

        default:
          return null;
      }
    })();

    return (
      <div className={`transition-all duration-300 ease-in-out ${isTransitioning ? 'transform translate-x-full opacity-0' : 'transform translate-x-0 opacity-100'}`}>
        {formContent}
      </div>
    );
  };

  const renderFooterLinks = () => {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => navigate('/login')}
        className="text-base font-medium"
      >
        Back to log in
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-nav flex flex-col relative overflow-hidden">
      {/* Glow effect - same as login page */}
      <div className="absolute inset-0 bg-gradient-radial from-primary/10 via-transparent to-transparent" style={{backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.15) 0%, transparent 50%)'}} />
      <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent" style={{backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(34, 197, 94, 0.1) 0%, transparent 40%)'}} />
      <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent" style={{backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.1) 0%, transparent 40%)'}} />
      
      <div className="flex-1 flex flex-col items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-xl">
          {/* Header outside card */}
          <div className="text-center mb-6 text-white animate-in fade-in slide-in-from-top-4 duration-500">
            <h1 className="text-4xl font-bold mb-2 tracking-tight">
              {getTitle()}
            </h1>
            <p className="text-base text-white/80">
              {getDescription()}
            </p>
          </div>
          
          <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <CardContent>
              {/* Back button */}
              {renderBackButton()}
              
              {error && <div className="mb-6 p-4 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>}
              {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg text-sm">{success}</div>}

              {renderForm()}

              <div className="flex flex-col items-center gap-4 mt-8">
                {renderFooterLinks()}
              </div>
            </CardContent>
          </Card>

          {/* Company attribution */}
          <p className="text-center mt-4 text-sm text-white/60">Service management for your business</p>
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center pb-6 relative z-10">
        <a href={getRutaWebsiteUrl()} target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
          <p className="text-white/70 text-[10px] uppercase tracking-wider">Powered by</p>
          <p className="text-white font-black text-2xl tracking-wider">RUTA</p>
        </a>
      </div>
    </div>
  );
}